import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { leaveTypeApi } from '../leaveApi';
import {
  LEAVE_CREDIT_KIND_LABELS,
  LeaveCreditKind,
  type LeaveCreditKindValue,
  type LeaveType,
} from '../leaveTypes';
import { sampleLeaveType } from '../utils/sampleLeaveType';

/**
 * Create or edit one leave type.
 *
 * No position: where it sits in the Credit Balance table is assigned by the server and changed
 * by moving the row, never by typing a number here — a rename is not a reorder.
 */
export function LeaveTypeModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null creates. */
  editing: LeaveType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seeded once, on mount. The parent mounts this only while it is open.
  const [name, setName] = useState(editing?.name ?? '');
  const [code, setCode] = useState(editing?.code ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [creditKind, setCreditKind] = useState<LeaveCreditKindValue>(
    editing?.creditKind ?? LeaveCreditKind.Ledger,
  );
  const [rate, setRate] = useState(
    editing?.monthlyAccrualDays == null ? '' : String(editing.monthlyAccrualDays),
  );

  /*
    THE PARENT, AND THE ONE QUESTION A CHILD MUST ANSWER — own count, or the parent's credit.
    Seeded EMPTY on a create: recording "own count" for a reader who said nothing would assert
    an answer nobody gave, so the wire carries `bool?` and the primary waits for it.
  */
  const [parentId, setParentId] = useState(editing?.parentLeaveTypeId ?? '');
  const [credit, setCredit] = useState<'' | 'own' | 'shared'>(
    editing?.parentLeaveTypeId == null ? '' : editing.sharesParentCredit ? 'shared' : 'own',
  );
  const [types, setTypes] = useState<LeaveType[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    leaveTypeApi
      .options(controller.signal)
      .then(setTypes)
      .catch(() => {
        if (!controller.signal.aborted) setTypes([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setTypesLoading(false);
      });

    return () => controller.abort();
  }, []);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
    ONE LEVEL DEEP: a parent is a top-level type other than this one, and a type that already
    has children cannot sit under another — the server refuses both, so neither is offered.
  */
  const hasChildren = (editing?.childCount ?? 0) > 0;
  // A granted type keeps no balance, so it cannot have children drawing on one.
  const sharedFrom = (editing?.sharingChildCount ?? 0) > 0;
  const parentOptions = types.filter((t) => t.parentLeaveTypeId == null && t.id !== editing?.id);
  const parent = types.find((t) => t.id === parentId) ?? null;
  const parentIsGrant = parent?.creditKind === LeaveCreditKind.Grant;
  const shares = parentId !== '' && credit === 'shared';

  // A sharing child is tracked as its parent is — a credit ledger — and accrues nothing itself.
  const effectiveKind = shares ? LeaveCreditKind.Ledger : creditKind;
  const isGrant = effectiveKind === LeaveCreditKind.Grant;
  const rateLocked = isGrant || shares;

  /*
    A GRANTED type keeps no running balance, so a monthly rate cannot mean anything for one —
    the server refuses it. The field is DRAWN ALWAYS and disabled for state, saying why in its
    own description: a required-looking control that appears and vanishes on a dropdown moves
    every field under it.
  */
  const rateValid = rate.trim() === '' || Number(rate) > 0;
  const valid =
    name.trim().length > 0
    && rateValid
    && (parentId === '' || credit !== '')
    && !(shares && parentIsGrant);
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const request = {
      name: name.trim(),
      code: code.trim() || null,
      description: description.trim() || null,
      creditKind: effectiveKind,
      // Blank is a real answer — "does not accrue by the calendar" — and not the same as zero.
      monthlyAccrualDays: rateLocked || rate.trim() === '' ? null : Number(rate),
      parentLeaveTypeId: parentId || null,
      sharesParentCredit: parentId ? credit === 'shared' : null,
    };

    try {
      if (editing) await leaveTypeApi.update(editing.id, request);
      else await leaveTypeApi.create(request);

      onSaved();
    } catch (caught) {
      const failure =
        caught instanceof ApiError ? caught : new ApiError('Could not save this leave type.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'leave-type' });
    }

    setSaving(false);
  }

  function generate() {
    const sample = sampleLeaveType();

    setName(sample.name);
    setCode(sample.code);
    setDescription(sample.description);
    setCreditKind(sample.creditKind);
    setRate(sample.monthlyAccrualDays);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit leave type' : 'New leave type'}
      size="lg"
      id="leave-type-modal"
      autogenerate={editing === null ? { onGenerate: generate, disabled: saving } : undefined}
      confirm={{
        id: 'leave-type-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="leave-type-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          <Button
            id="leave-type-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="leave-type-name"
            label="Name"
            required
            description="What the type is called on an employee's Credit Balance — Vacation Leave."
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={name.trim() ? error?.fieldError('name') : 'Enter a name.'}
            disabled={saving}
          />

          <TextField
            id="leave-type-code"
            label="Code"
            description="The short form already in use — VL, SL. Leave it blank if there is none."
            value={code}
            onChange={(e) => setCode(e.target.value)}
            error={error?.fieldError('code')}
            disabled={saving}
          />

          <Select
            id="leave-type-parent-leave-type-id"
            label="Parent type"
            description={
              hasChildren
                ? 'This type has child types of its own, so it cannot sit under another.'
                : 'The type this one sits under on the Credit Balance — Vacation Leave. Leave it blank for a top-level type.'
            }
            value={parentId}
            onChange={(next) => {
              setParentId(next);
              if (next === '') setCredit('');
            }}
            loading={typesLoading}
            error={error?.fieldError('parentLeaveTypeId')}
            disabled={saving || hasChildren}
            options={parentOptions.map((t) => ({
              value: t.id,
              label: t.name,
              sublabel: t.code ?? undefined,
              keywords: t.code ?? undefined,
            }))}
          />

          {/*
            Drawn always and DISABLED until a parent is chosen, saying why — a required question
            appearing from nowhere would move every field under it. A <Select> seeded EMPTY, not
            radios: two radio cards wrapped to three lines and stood this row twice the height
            of its neighbour, and an empty select is as unanswered as two clear radios.
          */}
          <Select
            id="leave-type-shares-parent-credit"
            label="Credit"
            required={parentId !== ''}
            placeholder="Choose…"
            clearable={false}
            description={
              parentId === ''
                ? 'Choose a parent type first. A top-level type always keeps its own count.'
                : `Own count keeps a separate balance. Sharing draws leave used from ${parent?.name ?? 'the parent'}'s balance.`
            }
            value={credit}
            onChange={(next) => setCredit(next as '' | 'own' | 'shared')}
            error={
              shares && parentIsGrant
                ? `${parent?.name ?? 'That type'} is a granted type and keeps no balance to share.`
                : error?.fieldError('sharesParentCredit')
            }
            disabled={saving || parentId === ''}
            options={[
              { value: 'own', label: 'Own count' },
              { value: 'shared', label: "Shares parent's credit", disabled: parentIsGrant },
            ]}
          />

          <Select
            id="leave-type-credit-kind"
            label="Tracked as"
            required
            description={
              shares
                ? "A type sharing its parent's credit is tracked as the parent is — a credit ledger."
                : sharedFrom
                  ? 'Child types share this credit, so it stays a credit ledger — a granted type keeps no balance to share.'
                  : 'A credit ledger keeps a running balance; a granted type is a whole entitlement with none.'
            }
            /* Numeric on the wire: String in, Number back out. */
            value={String(effectiveKind)}
            onChange={(next) => setCreditKind(Number(next) as LeaveCreditKindValue)}
            error={error?.fieldError('creditKind')}
            disabled={saving || shares}
            options={(Object.values(LeaveCreditKind) as LeaveCreditKindValue[]).map((value) => ({
              value: String(value),
              label: LEAVE_CREDIT_KIND_LABELS[value],
              disabled: value === LeaveCreditKind.Grant && sharedFrom,
            }))}
          />

          <TextField
            id="leave-type-monthly-accrual-days"
            label="Monthly accrual"
            description={
              shares
                ? "A type sharing its parent's credit does not accrue on its own — the parent earns it."
                : isGrant
                  ? 'A granted type keeps no running balance, so it does not accrue monthly.'
                  : 'Days earned per month — 1.25 under CSC rules. Blank if it does not accrue by the calendar.'
            }
            value={rateLocked ? '' : rate}
            onChange={(e) => setRate(e.target.value)}
            error={rateValid ? error?.fieldError('monthlyAccrualDays') : 'Enter a rate above zero, or leave it blank.'}
            disabled={saving || rateLocked}
          />
        </div>

        <TextField
          id="leave-type-description"
          label="Description"
          description="One line saying what the type is for, read by whoever maintains this catalog."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          error={error?.fieldError('description')}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}
